import { useEffect, useState, useCallback } from "react";
import "./ricerche.css";
import api from "../api";
import Table from "../Table/Table";
import { useNavigate } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import CustomSelect from "../CustomSelect";
import { buildFilterQueryParams } from "../utils/queryHelpers";
import useDebouncedValue from "../hooks/useDebouncedValue";

const OperatoreRicerche = () => {
  const [loading, setLoading] = useState(false);
  const [ricerches, setRicerches] = useState([]);
  const [sedi, setSedi] = useState([]);
  const [selectedSedeId, setSelectedSedeId] = useState(null);
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnFilterFns, setColumnFilterFns] = useState({});
  const debouncedSorting = useDebouncedValue(sorting, 400);
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 400);

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (userRole !== "OPERATOR") {
      alert("Accedi come operatore per visualizzare le ricerche!");
      navigate("/login");
      localStorage.clear();
    } else {
      setIsAuthorized(true);
      fetchSedi();
    }
  }, [navigate]);

  const fetchSedi = async () => {
    try {
      const response = await api.get("/sedi/me");

      if (response.status === 200 && response.data.length > 0) {
        setSedi(response.data);
        if (response.data.length === 1) {
          const onlySedeId = response.data[0].id;
          setSelectedSedeId(onlySedeId);
        }
      } else {
        toast.info("Non sei assegnato in nessun sedi!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
  };

  const fetchRicerche = useCallback(
    async ({
      sedeId,
      pageParam = page,
      sizeParam = pageSize,
      sortingParam = debouncedSorting,
      filtersParam = debouncedColumnFilters,
      filterFnsParam = columnFilterFns,
    } = {}) => {
      setLoading(true);
      try {
        const sortStrings = (sortingParam ?? []).map(
          ({ id, desc }) => `${id},${desc ? "desc" : "asc"}`
        );

        const filterParams = buildFilterQueryParams(
          filtersParam,
          filterFnsParam
        );

        const params = {
          page: pageParam,
          size: sizeParam,
          ...filterParams,
        };

        if (sortStrings.length > 0) {
          params.sort = sortStrings.join(";");
        }

        const endpoint = sedeId
          ? `/ricerche/me/sede/${sedeId}`
          : "/ricerche/me";

        const response = await api.get(endpoint, { params });

        if (response.status === 200) {
          const { content = [], page: pageInfo } = response.data ?? {};
          
          setRicerches(content);
          setTotalRows(pageInfo?.totalElements ?? content.length);
        } else {
          toast.error("Errore durante il recupero delle ricerche!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Bounce,
          });
        }
      } catch (error) {
        toast.error(error?.response?.data?.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, debouncedSorting, debouncedColumnFilters, columnFilterFns]
  );

  useEffect(() => {
    if (!isAuthorized) return;
    fetchRicerche({
      sedeId: selectedSedeId,
    });
  }, [isAuthorized, selectedSedeId, fetchRicerche]);

  const handleSedeChange = (e) => {
    const newSedeId = e.target.value ? Number(e.target.value) : null;
    setSelectedSedeId(newSedeId);
    setPage(0);
  };

  const columns = [
    { field: "sede", header: "Sede" },
    { field: "titoloCorso", header: "Titolo Corso" },
    { field: "titoloModulo", header: "Titolo Modulo" },
    { field: "ore", header: "Ore" },
    { field: "mansione", header: "Mansione" },
    { field: "statoRicerca", header: "Stato Ricerca" },
    { field: "docentiAssociati", header: "Docenti Associati" },
  ];

  if (!isAuthorized) return null;

  const handleRowClick = (ricerche) => {
    navigate(`/ricerca/${ricerche.id}`);
  };

  const handlePageChange = (pageNumber) => {
    setPage(Math.max(pageNumber - 1, 0));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
    setPage(0);
  };

  const handleColumnFiltersChange = (newFilters) => {
    setColumnFilters(newFilters);
    setPage(0);
  };

  const handleColumnFilterFnsChange = (newFilterFns) => {
    setColumnFilterFns(newFilterFns);
    setPage(0);
  };

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="page-header">
        <div className="page-title">Ricerche</div>
        <div className="controls">
          <div className="add-btn">
            <button onClick={() => navigate("/aggiungi-ricerca")}>
              + Nuova ricerca
            </button>
          </div>
        </div>
      </div>

      <div className="page-content ricerche">
        {sedi.length > 1 && (
          <div className="content-data operatore-ricerche">
            <div className="field-row">
              <div className="field-label">Seleziona Sede:</div>
              <div className="field-value">
                <CustomSelect
                  options={sedi.map((sede) => ({
                    value: String(sede.idSede),
                    label: sede.indirizzoSede,
                  }))}
                  value={
                    selectedSedeId !== null
                      ? {
                          value: String(selectedSedeId),
                          label:
                            sedi.find(
                              (s) => String(s.idSede) === String(selectedSedeId)
                            )?.indirizzoSede || "",
                        }
                      : null
                  }
                  onChange={(selected) =>
                    handleSedeChange({
                      target: {
                        name: "sede",
                        value: selected ? selected.value : "",
                      },
                    })
                  }
                  placeholder="-- Seleziona una sedi --"
                />
              </div>
            </div>
          </div>
        )}

        <Table
          columns={columns}
          data={ricerches}
          onRowClick={handleRowClick}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          totalRows={totalRows}
          perPage={pageSize}
          currentPage={page + 1}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          columnFilterFns={columnFilterFns}
          onColumnFilterFnsChange={handleColumnFilterFnsChange}
        />
      </div>
    </div>
  );
};

export default OperatoreRicerche;
